import { useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import Bugsnag from '@bugsnag/js'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import { authContext } from 'contexts/AuthContext'
import AdxPopper from 'components/reusable/AdxPopper'
import { logout } from 'services/api/authentication'
import defaultAvatar from 'assets/images/default_avatar.png'
import { ChevronRight, Logout, Person } from '@mui/icons-material'
import { useMediaQuery } from '@mui/material'
import { clearLocalSession } from 'services/session/authSession'

const AvatarPopper = () => {
  const queryClient = new useQueryClient()
  const { auth } = useContext(authContext)
  const navigate = useNavigate()
  const isLarge = useMediaQuery((theme) => theme.breakpoints.up('lg'))

  const navigateToProfile = () => {
    navigate(`/app/profile/${auth?.data?.staff?.id}`)
  }

  const navigateToWorkPersonalityFit = () => {
  navigate('/app/performance/assessment')
}

  const logOut = async () => {
    try {
      await logout()
    } catch (error) {
      Bugsnag.notify(error)
    } finally {
      clearLocalSession({ reason: 'logout' })
      Bugsnag.clearMetadata('tenant')
      queryClient.removeQueries()
      window.location.replace('/login')
    }
  }

  return (
    <AdxPopper
      customButton={
        <Box
          display={'flex'}
          alignItems={'center'}
          gap={2}
          border={isLarge && 1}
          borderColor={'gray-line.main'}
          borderRadius={'4.5px'}
          minHeight={{ lg: '59px', xs: 'fit-content' }}
          px={{ lg: 1.5, xs: 0 }}
          p={{ md: 0, xs: 0.75 }}
          bgcolor={'white.main'}
        >
          <Box
            width={{  xs: '40px' }}
            height={{  xs: '40px' }}
            overflow={'clip'}
            borderRadius={'50%'}
          >
            <img
              src={auth?.data?.profile_image_url || defaultAvatar}
              alt="logo"
              width={'40px'}
              height={'40px'}
              style={{objectFit:'cover'}}
            />
          </Box>

          <Box display={{ xs: 'none', lg: 'block' }} sx={{ maxWidth: '170px' }}>
            <Typography whiteSpace={'nowrap'} textOverflow={'ellipsis'} overflow={'hidden'}>
              {auth?.data?.staff?.name}
            </Typography>
            <Typography
              variant="body2"
              color="light-gray-text.main"
              whiteSpace={'nowrap'}
              textOverflow={'ellipsis'}
              overflow={'hidden'}
            >
              {auth?.data?.staff?.work_email}
            </Typography>
          </Box>
          <ChevronRight sx={{ display: { xs: 'none', lg: 'block' }, rotate: '90deg' }} />
        </Box>
      }
      placement="bottom-start"
      sx={{ marginTop: 1 }}
      linePlacement={[1, 2]}
    >
      <Box onClick={navigateToProfile} sx={{ display: 'flex', gap: 2 }}>
        <Person />
        <Typography>View Profile</Typography>
      </Box>
      <Box onClick={navigateToWorkPersonalityFit} sx={{ display: 'flex', gap: 2 }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="19" viewBox="0 0 20 19" fill="none">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M12.8179 2.38691C11.9351 1.18962 18.3381 4.18308 18.3384 7.77559C18.7064 8.77356 19.531 10.8093 19.8843 10.9689C20.3257 11.1687 19.4423 12.5663 18.3384 12.1672V14.7619C17.8231 15.4938 16.3508 16.8376 14.5845 16.3586V18.7531H5.53177V13.5637C5.09021 13.1646 4.20703 12.3273 4.20658 12.1672C5.605 12.2337 8.49099 12.2468 8.84427 11.7678C9.19799 11.2895 10.9049 12.5 11.7144 13.1652C12.8184 12.9656 14.9375 12.167 14.5845 10.5705L12.3765 8.37422C12.8181 6.77733 13.5244 3.34491 12.8179 2.38691ZM5.63431 0.00019555C6.67733 -0.00804265 7.70274 0.244169 8.59818 0.727735C9.49365 1.21137 10.2252 1.90816 10.7115 2.74238C11.1978 3.57664 11.4209 4.51605 11.356 5.45723C11.291 6.39836 10.9405 7.30519 10.3433 8.07832L13.0328 10.4689C13.2067 10.629 13.3042 10.8442 13.3042 11.0686C13.3042 11.293 13.2068 11.5091 13.0328 11.6691C12.8587 11.8292 12.6218 11.9211 12.3736 11.925C12.1254 11.9289 11.8856 11.844 11.7056 11.6896L9.0142 9.29805C8.28983 9.77207 7.45217 10.085 6.56888 10.2111C5.68567 10.3372 4.78183 10.2737 3.93119 10.0246C3.08041 9.77541 2.3061 9.34802 1.6724 8.77754C1.03875 8.20711 0.562946 7.50938 0.283726 6.74141C0.00455878 5.97347 -0.069779 5.15658 0.0659529 4.35762C0.201773 3.55854 0.544557 2.79962 1.06595 2.14277C1.58725 1.48606 2.27224 0.949512 3.06595 0.577344C3.85981 0.205197 4.7401 0.00702882 5.63431 0.00019555ZM5.65091 1.71309C4.64634 1.72084 3.6863 2.08895 2.98197 2.73652C2.27772 3.38414 1.88659 4.25811 1.89505 5.16621C1.90364 6.0743 2.31062 6.94259 3.02689 7.5793C3.74336 8.21601 4.71071 8.56946 5.71537 8.56172C6.71995 8.55396 7.67998 8.18586 8.38431 7.53828C9.08866 6.89058 9.47981 6.01585 9.47123 5.10762C9.46251 4.19955 9.05478 3.33213 8.33841 2.69551C7.62198 2.05886 6.6555 1.70538 5.65091 1.71309ZM5.75248 6.65254C6.02183 6.65261 6.24173 6.8734 6.24173 7.14277C6.24153 7.41198 6.0217 7.63197 5.75248 7.63203C5.4832 7.63203 5.26245 7.41202 5.26224 7.14277C5.26224 6.87336 5.48307 6.65254 5.75248 6.65254ZM5.74955 2.97871C6.32489 2.97871 6.71955 3.24052 6.92044 3.56855C7.09183 3.85013 7.19245 4.37691 6.92826 4.76875C6.63436 5.20227 6.35257 5.33524 6.20072 5.61445C6.1396 5.72695 6.1148 5.8004 6.11478 6.1623H5.40677C5.40429 5.97123 5.37564 5.66005 5.52494 5.39082C5.71357 5.05066 6.07611 4.85011 6.28666 4.54902C6.50951 4.23309 6.38425 3.64285 5.75248 3.64277C5.33857 3.64277 5.13507 3.9564 5.04935 4.21602L4.41556 3.94844C4.58949 3.42695 5.06164 2.97887 5.74955 2.97871Z" fill="#3A3A3A"/>
        </svg>
        <Typography>Work Personality Fit</Typography>
      </Box>
      <Box onClick={logOut} sx={{ display: 'flex', gap: 2 }}>
        <Logout />
        <Typography>Sign Out</Typography>
      </Box>
    </AdxPopper>
  )
}

export default AvatarPopper
